import {isEqual} from 'lodash';
import {useMemo} from 'react';
import {createSelector} from 'reselect';

import {type CommentableItem} from '../../components/ReportDiscussion/types';
import {
  isPanelGrid,
  type PanelGrid,
} from '../../components/Slate/plugins/panel-grid';
import {type RootState} from '../../types/redux';
import {useMemoizedSelector} from '../hooks';
import {usePart, useParts} from '../views/hooks';
import {lookupPart} from '../views/normalizerSupport';
import {
  type ReportPageNormalized,
  type ReportWidthOption,
} from '../views/report/types';
import type * as Types from './types';
import {isReportRef} from './types';

export const getReportsState = (state: RootState) => state.reports;

export function makeReportViewIDSelector(reportID: string | undefined) {
  return createSelector(getReportsState, reports => {
    if (reportID == null) {
      // oxlint-disable-next-line @typescript-eslint/prefer-as-const
      return {loading: true as true};
    }
    const viewID = reports.viewRefs[reportID];
    if (viewID == null) {
      // oxlint-disable-next-line @typescript-eslint/prefer-as-const
      return {loading: true as true};
    }
    // oxlint-disable-next-line @typescript-eslint/prefer-as-const
    return {loading: false as false, viewID};
  });
}

export function makePushedReportSelector() {
  return createSelector(getReportsState, reports => reports.pushedRef);
}

export const getReportID =
  (reportViewRef: Types.ReportViewRef) => (state: RootState) => {
    const view = state.views.views[reportViewRef.id];
    if (view == null || view.partRef == null) {
      throw new Error('invalid state');
    }
    if (!isReportRef(view.partRef)) {
      throw new Error('invalid state');
    }
    if (view.id == null) {
      throw new Error('invalid state');
    }
    return view.id;
  };

export const getReportParentID =
  (reportViewRef: Types.ReportViewRef) => (state: RootState) => {
    const view = state.views.views[reportViewRef.id];
    if (view == null || view.partRef == null) {
      throw new Error('invalid state');
    }
    if (!isReportRef(view.partRef)) {
      throw new Error('invalid state');
    }
    return view.parentId;
  };

export const getReportEntityName =
  (reportViewRef: Types.ReportViewRef) => (state: RootState) => {
    const view = state.views.views[reportViewRef.id];
    if (view == null || view.partRef == null) {
      throw new Error('invalid state');
    }
    if (!isReportRef(view.partRef)) {
      throw new Error('invalid state');
    }
    return view.project?.entityName ?? view.entityName;
  };

export const getReportRef =
  (reportViewRef: Types.ReportViewRef) => (state: RootState) => {
    const view = state.views.views[reportViewRef.id];
    if (view == null || view.partRef == null) {
      throw new Error('invalid state');
    }
    if (!isReportRef(view.partRef)) {
      throw new Error('invalid state');
    }
    return view.partRef;
  };

export const getReportPart =
  (reportViewRef: Types.ReportViewRef) =>
  (state: RootState): ReportPageNormalized => {
    const view = state.views.views[reportViewRef.id];
    if (view == null || view.partRef == null) {
      throw new Error('invalid state');
    }
    if (!isReportRef(view.partRef)) {
      throw new Error('invalid state');
    }
    return lookupPart(state.views.parts, view.partRef);
  };

export const getReportPanelSettingsRef =
  (reportViewRef: Types.ReportViewRef) => (state: RootState) => {
    const reportPart = getReportPart(reportViewRef)(state);
    if (reportPart.panelSettingsRef == null) {
      // We define this by default so it should always be available.
      throw new Error('invalid state');
    }
    return reportPart.panelSettingsRef;
  };

export const getReportWidth =
  (reportViewRef?: Types.ReportViewRef) => (state: RootState) => {
    if (reportViewRef == null) {
      return 'readable' as ReportWidthOption; // Default to readable if it's null
    }
    const reportPart = getReportPart(reportViewRef)(state);
    return reportPart.width;
  };

export const getReportBlocks =
  (reportViewRef: Types.ReportViewRef) => (state: RootState) => {
    const reportPart = getReportPart(reportViewRef)(state);
    return reportPart.blocks;
  };

// This returns all the panels in the report.
// Used as panel lookup for comment references
export const makeReportPanelsSelector = (
  reportViewRef: Types.ReportViewRef
) => {
  let reportPanels: CommentableItem[] | undefined;
  let prevReportPanelGridElements: PanelGrid[] | undefined;
  return () => {
    const reportRef = useMemoizedSelector(getReportRef, reportViewRef);
    const report = usePart(reportRef);
    const reportPanelGridElements = report.blocks.filter(b =>
      isPanelGrid(b)
    ) as PanelGrid[];
    if (
      reportPanels == null ||
      !isEqual(reportPanelGridElements, prevReportPanelGridElements)
    ) {
      prevReportPanelGridElements = reportPanelGridElements;
      reportPanels = reportPanelGridElements
        .map(block => block.metadata.panelBankSectionConfig.panels)
        .flat() as CommentableItem[];
    }
    return reportPanels;
  };
};

export const getReportDiscussionThreadRefs =
  (reportViewRef: Types.ReportViewRef) => (state: RootState) => {
    const reportPart = getReportPart(reportViewRef)(state);
    return reportPart.discussionThreadRefs;
  };

export const getReportDiscussionCommentsCount =
  (reportViewRef: Types.ReportViewRef) => () => {
    const reportRef = useMemoizedSelector(getReportRef, reportViewRef);
    const report = usePart(reportRef);
    const discussionThreads = useParts(report.discussionThreadRefs);
    const commentsCount = useMemo(() => {
      return discussionThreads.reduce(
        (count: number, thread) => count + thread.commentRefs.length,
        0
      );
    }, [discussionThreads]);
    return commentsCount;
  };
